/* 
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN 
 * Este archivo contiene la lógica de listado de contactos y acciones masivas con confirmación y agrupación de etiquetas.
 * Implementado por: Trae AI
 * Fecha: 05/03/2026
 */
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  IconButton, 
  TextField, 
  InputAdornment, 
  makeStyles,
  Avatar,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  Menu,
  Popover,
  Grid,
  Box,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { 
  Search as SearchIcon, 
  DeleteOutline as DeleteOutlineIcon, 
  Edit as EditIcon, 
  AddCircle as AddCircleIcon,
  GetApp as ExportIcon,
  CloudUpload as ImportIcon,
  FilterList as FilterListIcon,
  Check as CheckIcon
} from "@material-ui/icons";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { toast } from "react-toastify";
import ContactModal from "../../components/ContactModal";
import ContactDetailsModal from "../../components/ContactDetailsModal";
import ImportContacts from "./ImportContacts";
import LabelsManager from "./LabelsManager";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import { format } from "date-fns";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.default,
  },
  toolbarPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  searchContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center', // Para mantener el buscador en el centro
  },
  tablePaper: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
  },
  table: {
    minWidth: 650,
  },
  actionCell: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  filterButton: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    textTransform: "none",
    fontWeight: 600,
  },
  importButton: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    textTransform: "none",
    fontWeight: 600,
    marginRight: theme.spacing(1),
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    }
  },
  exportButton: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    textTransform: "none",
    fontWeight: 600,
    marginRight: theme.spacing(1),
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    }
  },
  createButton: {
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
        backgroundColor: theme.palette.success.dark,
    }
  },
  filterMenu: {
    minWidth: 200,
  },
  chipText: {
    color: "inherit !important",
  }
}));

const getContrastText = (hex) => {
  if (!hex) return '#1a1a2e';
  // Remove # if present
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#1a1a2e' : '#ffffff';
};

export default function Contacts() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParam, setSearchParam] = useState("");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactDetailsModalOpen, setContactDetailsModalOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [labelsManagerOpen, setLabelsManagerOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmBatchDeleteOpen, setConfirmBatchDeleteOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  
  const [labels, setLabels] = useState([]);
  const [filterLabel, setFilterLabel] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [batchLabelModalOpen, setBatchLabelModalOpen] = useState(false);
  const [localBatchLabels, setLocalBatchLabels] = useState([]);
  const [initialBatchLabels, setInitialBatchLabels] = useState([]);
  const [selectedBatchLabelId, setSelectedBatchLabelId] = useState("");
  const [batchConnectionAnchorEl, setBatchConnectionAnchorEl] = useState(null);
  const [folders, setFolders] = useState([]);
  const [confirmBatchConnectionOpen, setConfirmBatchConnectionOpen] = useState(false);
  const [selectedBatchConnection, setSelectedBatchConnection] = useState(null);

  // Filter Menu State
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState("labels"); // labels, connection, date
  const [whatsApps, setWhatsApps] = useState([]);
  const [filterConnection, setFilterConnection] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const connectionColors = {
    0: "#FFD1DC", // Pastel Pink
    1: "#AEC6CF", // Pastel Blue
    2: "#77DD77", // Pastel Green
    3: "#FDFD96", // Pastel Yellow
    4: "#B39EB5", // Pastel Purple
    5: "#FFB347", // Pastel Orange
  };

  const getConnectionColor = (index) => {
    return connectionColors[index % 6];
  };

  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchFromUrl = params.get("search");
    if (searchFromUrl) {
      setSearchParam(searchFromUrl);
      // Opcional: limpiar la URL para que no se quede ahí permanentemente
      history.replace("/contacts");
    }
  }, [location.search, history]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/labels');
        setLabels(data);
      } catch (e) {
        toast.error('Error cargando etiquetas');
      }

      try {
        const { data: whatsappData } = await api.get('/whatsapp');
        setWhatsApps(whatsappData);
      } catch (e) {
        toast.error('Error cargando conexiones');
      }

      try {
        const { data: settingsRes } = await api.get('/settings');
        const folderRegistry = settingsRes.find(s => s.key === "tagFolders");
        if (folderRegistry && folderRegistry.value) {
            setFolders(JSON.parse(folderRegistry.value));
        }
      } catch (e) {
        console.error('Error cargando carpetas', e);
      }
    })();
  }, []);

  const groupedLabels = React.useMemo(() => {
    const groups = {};
    const systemFolderId = 'system_folder';
    const unassignedId = 'unassigned_folder';

    groups[systemFolderId] = { name: 'Sistema', items: [] };
    folders.forEach(f => {
        if (f.name !== 'Sistema') {
            groups[f.id] = { name: f.name, items: [] };
        }
    });
    groups[unassignedId] = { name: 'Sin Carpeta', items: [] };

    labels.forEach(l => {
        if (l.isSystem) {
            groups[systemFolderId].items.push(l);
        } else if (l.folderId && groups[l.folderId]) {
            groups[l.folderId].items.push(l);
        } else {
            groups[unassignedId].items.push(l);
        }
    });

    return groups;
  }, [labels, folders]);

  const allLabels = React.useMemo(() => {
    const flattened = [];
    Object.keys(groupedLabels).forEach(key => {
        const group = groupedLabels[key];
        group.items.forEach(label => {
            flattened.push({ ...label, groupName: group.name });
        });
    });
    return flattened;
  }, [groupedLabels]);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/contacts", {
        params: { searchParam, pageNumber, labelId: filterLabel, whatsappId: filterConnection, startDate, endDate }
      });
      // Existing API returns { contacts, count, hasMore }
      if (pageNumber === 1) {
        setContacts(data.contacts);
      } else {
        setContacts(prev => [...prev, ...data.contacts]);
      }
      setTotalContacts(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  }, [searchParam, pageNumber, filterLabel, filterConnection, startDate, endDate]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, fetchContacts, filterLabel, pageNumber]);

  useEffect(() => {
    setPageNumber(1);
  }, [searchParam, filterLabel, filterConnection, startDate, endDate]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMore();
    }
  };

  const handleDeleteContact = async () => {
    try {
      await api.delete(`/contacts/${contactToDelete.id}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      setContacts(contacts.filter((c) => c.id !== contactToDelete.id));
    } catch (err) {
      toast.error("Error al eliminar contacto");
    }
    setConfirmDeleteOpen(false);
  };

  const handleEditClick = (contact) => {
    setSelectedContactId(contact.id);
    setContactModalOpen(true);
  };

  const handleDetailsClick = (contact) => {
    setSelectedContactId(contact.id);
    setContactDetailsModalOpen(true);
  };

  const handleSelectAll = async (event) => {
    if (event.target.checked) {
      toast.info("Seleccionando todos los contactos que coinciden con la búsqueda actual...");
      try {
        setLoading(true);
        // We fetch ALL matching contacts IDs for the current filters
        const { data } = await api.get("/contacts", {
          params: { searchParam, pageNumber: 1, labelId: filterLabel, whatsappId: filterConnection, startDate, endDate, limit: 100000 }
        });
        const newSelecteds = data.contacts.map((n) => n.id);
        setSelectedContacts(newSelecteds);
        toast.success(`Se han seleccionado ${newSelecteds.length} contactos`);
      } catch (err) {
        toast.error("Error al seleccionar contactos masivamente");
      } finally {
        setLoading(false);
      }
      return;
    }
    setSelectedContacts([]);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedContacts.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedContacts, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedContacts.slice(1));
    } else if (selectedIndex === selectedContacts.length - 1) {
      newSelected = newSelected.concat(selectedContacts.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedContacts.slice(0, selectedIndex),
        selectedContacts.slice(selectedIndex + 1),
      );
    }
    setSelectedContacts(newSelected);
  };

  const handleExportContacts = async () => {
    try {
      toast.info("Generando informe, por favor espera...");
      
      const { data } = await api.get("/contacts/export", {
        params: { 
          searchParam, 
          labelId: filterLabel, 
          whatsappId: filterConnection, 
          startDate, 
          endDate 
        }
      });

      if (!data || data.length === 0) {
        toast.warn("No hay contactos para exportar con estos filtros.");
        return;
      }

      const excelData = data.map(contact => {
        const labelsStr = contact.labels ? contact.labels.map(l => l.name).join(", ") : "";
        const connectionStr = contact.whatsapp ? contact.whatsapp.name : "Sin conexión";
        const createdAt = contact.createdAt ? new Date(contact.createdAt).toLocaleDateString("es-CO") : "";

        return {
          "Usuario": contact.name || "",
          "Número telefónico": contact.number || "",
          "Etiquetas": labelsStr,
          "Fecha de inscripción": createdAt,
          "Conexión": connectionStr
        };
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contactos");
      XLSX.writeFile(wb, "Informe_Contactos_Lasit.xlsx");

      toast.success("Informe descargado con éxito");
    } catch (err) {
      toast.error("Error al descargar el informe");
      console.error(err);
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleBatchDelete = () => {
    if (selectedContacts.length === 0) return;
    setConfirmBatchDeleteOpen(true);
  };

  const executeBatchDelete = async () => {
    try {
        setLoading(true);
        // Sequential delete for now as no batch endpoint exists
        for (const id of selectedContacts) {
            await api.delete(`/contacts/${id}`);
        }
        toast.success(`${selectedContacts.length} contactos eliminados`);
        setSelectedContacts([]);
        fetchContacts();
    } catch (err) {
        toast.error("Error eliminando contactos");
        setLoading(false);
    }
    setConfirmBatchDeleteOpen(false);
  };

  const handleAddBatchLabelToLocalState = () => {
    if (!selectedBatchLabelId) return;
    const labelToAdd = allLabels.find(l => l.id === selectedBatchLabelId);
    if (!labelToAdd) return;

    if (!localBatchLabels.find(l => l.id === labelToAdd.id)) {
        setLocalBatchLabels([...localBatchLabels, labelToAdd]);
    }
    setSelectedBatchLabelId("");
  };

  const handleRemoveBatchLabelFromLocalState = (id) => {
    setLocalBatchLabels(localBatchLabels.filter(l => l.id !== id));
  };

  const handleOpenBatchLabelModal = () => {
    if (selectedContacts.length === 0) return;
    
    // Calcular todas las etiquetas existentes (Unión)
    const selectedContactObjects = selectedContacts.map(id => contacts.find(c => c.id === id)).filter(Boolean);
    
    const uniqueLabelsMap = new Map();
    
    selectedContactObjects.forEach(contact => {
        if (contact.labels) {
            contact.labels.forEach(label => {
                if (!uniqueLabelsMap.has(label.id)) {
                    uniqueLabelsMap.set(label.id, label);
                }
            });
        }
    });
    
    const allExistingLabels = Array.from(uniqueLabelsMap.values());
    
    setInitialBatchLabels(allExistingLabels);
    setLocalBatchLabels(allExistingLabels);
    setBatchLabelModalOpen(true);
  };

  const executeBatchLabelsSave = async () => {
    if (selectedContacts.length === 0) return;

    try {
        setLoading(true);
        
        // Calcular diferencias
        const labelsToAdd = localBatchLabels.filter(ll => !initialBatchLabels.some(il => il.id === ll.id));
        const labelsToRemove = initialBatchLabels.filter(il => !localBatchLabels.some(ll => ll.id === il.id));

        for (const id of selectedContacts) {
            const contact = contacts.find(c => c.id === id);
            let currentLabelIds = contact?.labels ? contact.labels.map(l => l.id) : [];
            
            // Quitar las eliminadas
            currentLabelIds = currentLabelIds.filter(lid => !labelsToRemove.some(lr => lr.id === lid));
            
            // Sumar las nuevas
            labelsToAdd.forEach(la => {
                if (!currentLabelIds.includes(la.id)) currentLabelIds.push(la.id);
            });
            
            await api.put(`/contacts/${id}`, {
                labelIds: currentLabelIds
            });
        }
        toast.success(`Etiquetas actualizadas en ${selectedContacts.length} contactos`);
        setSelectedContacts([]);
        fetchContacts();
    } catch (err) {
        toast.error("Error actualizando etiquetas");
        setLoading(false);
    }
    setBatchLabelModalOpen(false);
    setLocalBatchLabels([]);
    setInitialBatchLabels([]);
  };

  const handleBatchConnection = (connection) => {
    setBatchConnectionAnchorEl(null);
    setSelectedBatchConnection(connection);
    setConfirmBatchConnectionOpen(true);
  };

  const executeBatchConnection = async () => {
    if (selectedContacts.length === 0 || !selectedBatchConnection) return;

    try {
        setLoading(true);
        for (const id of selectedContacts) {
             await api.put(`/contacts/${id}`, {
                 whatsappId: selectedBatchConnection.id
             });
        }
        toast.success(`Conexión actualizada para ${selectedContacts.length} contactos`);
        setSelectedContacts([]);
        fetchContacts();
    } catch (err) {
        toast.error("Error actualizando conexiones");
        setLoading(false);
    }
    setConfirmBatchConnectionOpen(false);
    setSelectedBatchConnection(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("contacts.confirmationModal.deleteTitle")}
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteContact}
      >
        {i18n.t("contacts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("contacts.confirmationModal.deleteTitle")}
        open={confirmBatchDeleteOpen}
        onClose={() => setConfirmBatchDeleteOpen(false)}
        onConfirm={executeBatchDelete}
      >
        ¿Estás seguro de que deseas eliminar los {selectedContacts.length} contactos seleccionados? Esta acción no se puede deshacer.
      </ConfirmationModal>

      <Dialog open={batchLabelModalOpen} onClose={() => setBatchLabelModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>{selectedContacts.length} seleccionados</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box style={{ marginBottom: 24 }}>
            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>Etiquetas</Typography>
            
            <Box style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
              <Autocomplete
                fullWidth
                size="small"
                options={allLabels}
                groupBy={(option) => option.groupName}
                getOptionLabel={(option) => option.name}
                value={allLabels.find(l => l.id === selectedBatchLabelId) || null}
                onChange={(e, newValue) => {
                    setSelectedBatchLabelId(newValue ? newValue.id : "");
                }}
                renderOption={(option) => {
                    const optColor = option.isSystem ? "#FFB09C" : (option.color || '#ddd');
                    return (
                        <Chip 
                            label={option.name}
                            size="small"
                            style={{ 
                                backgroundColor: optColor, 
                                color: getContrastText(optColor),
                                cursor: 'pointer'
                            }}
                            classes={{ label: classes.chipText }}
                        />
                    );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Buscar y asignar etiquetas"
                  />
                )}
              />
              <Button variant="outlined" onClick={handleAddBatchLabelToLocalState}>
                ASIGNAR
              </Button>
            </Box>

            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {localBatchLabels.map(tag => {
                const optColor = tag.isSystem ? "#FFB09C" : (tag.color || '#ddd');
                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    onDelete={() => handleRemoveBatchLabelFromLocalState(tag.id)}
                    style={{ 
                        backgroundColor: optColor, 
                        color: getContrastText(optColor), 
                    }}
                    size="small"
                    classes={{ label: classes.chipText, deleteIcon: classes.chipText }}
                  />
                );
              })}
            </Box>
          </Box>
        </DialogContent>
         <DialogActions style={{ justifyContent: 'space-between', padding: 16 }}>
            <Button onClick={() => { setBatchLabelModalOpen(false); setLocalBatchLabels([]); setInitialBatchLabels([]); }} style={{ color: '#2576d2' }}>
               CERRAR
            </Button>
            <Button 
               variant="contained" 
              color="primary" 
              onClick={executeBatchLabelsSave}
              disabled={localBatchLabels.length === 0}
           >
              GUARDAR
           </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Confirmar cambio de conexión masivo"
        open={confirmBatchConnectionOpen}
        onClose={() => setConfirmBatchConnectionOpen(false)}
        onConfirm={executeBatchConnection}
      >
        ¿Estás seguro? Estás a punto de asignar la conexión <strong>"{selectedBatchConnection?.name}"</strong> a <strong>{selectedContacts.length}</strong> contactos.
      </ConfirmationModal>

      <ImportContacts 
        open={importModalOpen} 
        onClose={() => setImportModalOpen(false)} 
        onImport={fetchContacts}
      />

      <LabelsManager
        open={labelsManagerOpen}
        onClose={() => {
            setLabelsManagerOpen(false);
            (async () => {
                try {
                    const { data } = await api.get('/labels');
                    setLabels(data);
                } catch (e) { console.error(e); }
            })();
        }}
      />

      <ContactModal
        open={contactModalOpen}
        onClose={() => {
          setContactModalOpen(false);
          setSelectedContactId(null);
        }}
        contactId={selectedContactId}
        onSave={fetchContacts}
      />

      <ContactDetailsModal
        open={contactDetailsModalOpen}
        onClose={() => {
          setContactDetailsModalOpen(false);
          setSelectedContactId(null);
          fetchContacts(); // Forzar actualización al cerrar
        }}
        contactId={selectedContactId}
        onEdit={(contact) => {
            setContactDetailsModalOpen(false);
            setContactModalOpen(true);
        }}
      />

      <MainHeader>
        <Title>Contactos</Title>
      </MainHeader>

      <Paper className={classes.toolbarPaper} elevation={1}>
        {/* Botones principales */}
        <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            className={classes.createButton}
            onClick={() => {
              setSelectedContactId(null);
              setContactModalOpen(true);
            }}
        >
            CREAR CONTACTO
        </Button>
        {user.profile === "admin" && (
            <>
                <Button
                    variant="contained"
                    startIcon={<ImportIcon />}
                    className={classes.importButton}
                    onClick={() => setImportModalOpen(true)}
                >
                    IMPORTAR CONTACTOS
                </Button>
                <Button
                    variant="contained"
                    startIcon={<ExportIcon />}
                    className={classes.exportButton}
                    onClick={handleExportContacts}
                >
                    DESCARGAR INFORME
                </Button>
            </>
        )}
      </Paper>

      <Paper className={classes.tablePaper} elevation={1} onScroll={handleScroll} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
             <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                 {selectedContacts.length > 0 ? (
                     <>
                        <Typography variant="subtitle1" style={{ marginRight: 16 }}>
                            {selectedContacts.length} seleccionados
                        </Typography>
                                {user.profile === "admin" && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            style={{ color: 'red', borderColor: 'red', marginRight: 8 }}
                                            startIcon={<DeleteOutlineIcon />}
                                            onClick={handleBatchDelete}
                                        >
                                            Eliminar
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FilterListIcon />}
                                            onClick={(e) => setBatchConnectionAnchorEl(e.currentTarget)}
                                            style={{ marginRight: 8 }}
                                        >
                                            Cambiar Conexión
                                        </Button>
                                        <Menu
                                            anchorEl={batchConnectionAnchorEl}
                                            keepMounted
                                            open={Boolean(batchConnectionAnchorEl)}
                                            onClose={() => setBatchConnectionAnchorEl(null)}
                                        >
                                            <ListSubheader style={{ fontWeight: 'bold', lineHeight: '32px', backgroundColor: '#f5f5f5' }}>
                                                Seleccionar Conexión
                                            </ListSubheader>
                                            {whatsApps.map((w, index) => (
                                                <MenuItem key={w.id} onClick={() => handleBatchConnection(w)}>
                                                    <Chip 
                                                        label={w.name} 
                                                        size="small"
                                                        style={{ 
                                                            backgroundColor: getConnectionColor(index), 
                                                            color: getContrastText(getConnectionColor(index)) 
                                                        }}
                                                        classes={{ label: classes.chipText, deleteIcon: classes.chipText }} 
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                )}
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={handleOpenBatchLabelModal}
                            style={{ marginRight: 16 }}
                        >
                            Cambiar o agregar etiquetas
                        </Button>
                     </>
                 ) : (
                     <>
                         <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            className={classes.filterButton}
                            onClick={handleFilterClick}
                            style={{ marginRight: 16 }}
                        >
                            FILTROS
                        </Button>
                        <Popover
                            open={Boolean(filterAnchorEl)}
                            anchorEl={filterAnchorEl}
                            onClose={handleFilterClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <Box style={{ display: 'flex', width: 500, height: 400 }}>
                                <Box style={{ width: '40%', borderRight: '1px solid #eee', padding: '8px 0' }}>
                                   <Typography variant="subtitle2" style={{ padding: '8px 16px', fontWeight: 'bold' }}>Filtro</Typography>
                                   <MenuItem 
                                        selected={filterType === "labels"}
                                        onClick={() => setFilterType("labels")}
                                        style={{ 
                                            justifyContent: 'space-between', 
                                            backgroundColor: filterType === "labels" ? '#e3f2fd' : 'transparent', 
                                            borderLeft: filterType === "labels" ? '4px solid #2196f3' : '4px solid transparent'
                                        }}
                                   >
                                      <span style={{ display: 'flex', alignItems: 'center' }}>
                                         <CheckIcon style={{ color: filterType === "labels" ? 'green' : 'gray', marginRight: 8, fontSize: 20 }} />
                                         Etiqueta
                                      </span>
                                   </MenuItem>
                                   {user.profile === "admin" && (
                                       <MenuItem 
                                            selected={filterType === "connection"}
                                            onClick={() => setFilterType("connection")}
                                            style={{ 
                                                justifyContent: 'space-between', 
                                                backgroundColor: filterType === "connection" ? '#e3f2fd' : 'transparent', 
                                                borderLeft: filterType === "connection" ? '4px solid #2196f3' : '4px solid transparent'
                                            }}
                                       >
                                          <span style={{ display: 'flex', alignItems: 'center' }}>
                                             <CheckIcon style={{ color: filterType === "connection" ? 'green' : 'gray', marginRight: 8, fontSize: 20 }} />
                                             Conexión
                                          </span>
                                       </MenuItem>
                                   )}
                                   <MenuItem 
                                        selected={filterType === "date"}
                                        onClick={() => setFilterType("date")}
                                        style={{ 
                                            justifyContent: 'space-between', 
                                            backgroundColor: filterType === "date" ? '#e3f2fd' : 'transparent', 
                                            borderLeft: filterType === "date" ? '4px solid #2196f3' : '4px solid transparent'
                                        }}
                                   >
                                      <span style={{ display: 'flex', alignItems: 'center' }}>
                                         <CheckIcon style={{ color: filterType === "date" ? 'green' : 'gray', marginRight: 8, fontSize: 20 }} />
                                         Fecha
                                      </span>
                                   </MenuItem>
                                   <Typography variant="caption" style={{ padding: '16px', display: 'block', color: '#9e9e9e' }}>Seleccione una categoría</Typography>
                                </Box>
                                <Box style={{ width: '60%', display: 'flex', flexDirection: 'column', padding: '16px' }}>
                                   {filterType === "labels" && (
                                     <>
                                       <Box style={{ marginBottom: 16 }}>
                                         <TextField 
                                            placeholder="Buscar o ingresar valor" 
                                            variant="outlined" 
                                            size="small" 
                                            fullWidth 
                                            InputProps={{
                                                startAdornment: (
                                                  <InputAdornment position="start">
                                                    <SearchIcon style={{ color: "gray" }} />
                                                  </InputAdornment>
                                                ),
                                              }}
                                         />
                                       </Box>
                                       <Box style={{ overflowY: 'auto', flex: 1 }}>
                                          <Typography variant="caption" style={{ display: 'block', marginBottom: 8, color: '#9e9e9e' }}>Resultados</Typography>
                                          <MenuItem 
                                              onClick={() => { setFilterLabel(""); handleFilterClose(); }}
                                              selected={!filterLabel}
                                          >
                                              Sin filtro
                                          </MenuItem>
                                          {labels
                                             .filter(l => {
                                                 // Muro de Cristal: si es admin se muestra todo.
                                                 // Si es user, validamos que la etiqueta pertenezca a una carpeta asignada o a sistema si la carpeta de sistema fue asignada.
                                                 if (user.profile === "admin") return true;
                                                 
                                                 let assignedFolderIds = [];
                                                 try {
                                                    assignedFolderIds = typeof user.assignedFolders === "string" 
                                                        ? JSON.parse(user.assignedFolders) 
                                                        : (user.assignedFolders || []);
                                                 } catch (e) {
                                                    assignedFolderIds = [];
                                                 }

                                                 // Si es etiqueta de sistema y tiene asignada la carpeta virtual "system_folder"
                                                 if (l.isSystem && assignedFolderIds.includes("system_folder")) return true;
                                                 
                                                 // Si es una etiqueta normal, validamos si su carpeta está asignada
                                                 return assignedFolderIds.includes(l.folderId);
                                             })
                                             .map(l => {
                                                const lblColor = l.isSystem ? "#FFB09C" : (l.color || "#e0e0e0");
                                                return (
                                                 <MenuItem 
                                                    key={l.id} 
                                                    onClick={() => { setFilterLabel(l.id); handleFilterClose(); }}
                                                    selected={filterLabel === l.id}
                                                 >
                                                    <Chip 
                                                        label={l.name} 
                                                        size="small"
                                                        style={{ 
                                                            backgroundColor: lblColor, 
                                                            color: getContrastText(lblColor) 
                                                        }}
                                                        classes={{ label: classes.chipText, deleteIcon: classes.chipText }} 
                                                    />
                                                 </MenuItem>
                                                );
                                          })}
                                       </Box>
                                     </>
                                   )}
                                   {filterType === "connection" && (
                                     <Box style={{ overflowY: 'auto', flex: 1 }}>
                                        <Typography variant="caption" style={{ display: 'block', marginBottom: 8, color: '#9e9e9e' }}>Resultados</Typography>
                                        <MenuItem 
                                            onClick={() => { setFilterConnection(""); handleFilterClose(); }}
                                            selected={!filterConnection}
                                        >
                                            Sin filtro
                                        </MenuItem>
                                        {whatsApps.map((w, index) => (
                                           <MenuItem 
                                              key={w.id} 
                                              onClick={() => { setFilterConnection(w.id); handleFilterClose(); }}
                                              selected={filterConnection === w.id}
                                           >
                                              <Chip 
                                                label={w.name} 
                                                size="small"
                                                style={{ 
                                                    backgroundColor: getConnectionColor(index), 
                                                    color: getContrastText(getConnectionColor(index)) 
                                                }}
                                                classes={{ label: classes.chipText, deleteIcon: classes.chipText }} 
                                              />
                                           </MenuItem>
                                        ))}
                                     </Box>
                                   )}
                                   {filterType === "date" && (
                                     <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        <Typography variant="caption" style={{ display: 'block', marginBottom: 8, color: '#9e9e9e' }}>Rango de Fechas</Typography>
                                        <TextField
                                            label="Fecha Inicio"
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Fecha Fin"
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={handleFilterClose}
                                            disabled={!startDate && !endDate}
                                        >
                                            Aplicar Filtro
                                        </Button>
                                        <Button 
                                            variant="outlined" 
                                            color="secondary" 
                                            onClick={() => { setStartDate(""); setEndDate(""); handleFilterClose(); }}
                                        >
                                            Limpiar Fecha
                                        </Button>
                                     </Box>
                                   )}
                                </Box>
                            </Box>
                        </Popover>
                     </>
                 )}
                 <Box className={classes.searchContainer}>
                     <TextField
                         placeholder="Buscar (ej. Perez, Juan Perez, 310123)"
                         variant="outlined"
                         size="small"
                         value={searchParam}
                         onChange={(e) => setSearchParam(e.target.value)}
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <SearchIcon style={{ color: "gray" }} />
                             </InputAdornment>
                           ),
                         }}
                         style={{ width: '100%', minWidth: 250 }}
                     />
                 </Box>
             </Box>
             <Chip 
                label={`Contactos: ${totalContacts}`} 
                color="primary" 
                variant="outlined"
                style={{ backgroundColor: '#0d1117', color: '#fff', border: 'none', fontWeight: 'bold' }}
             />
        </Box>
        <Table className={classes.table}>
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                            checked={contacts.length > 0 && selectedContacts.length === contacts.length}
                            onChange={handleSelectAll}
                        />
                    </TableCell>
                    <TableCell>Usuarios</TableCell>
                    <TableCell>No Telefónico</TableCell>
                    <TableCell>Etiquetas</TableCell>
                    <TableCell>Fecha de inscripción</TableCell>
                    <TableCell>Conexión</TableCell>
                    <TableCell>Acciones</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {contacts.length === 0 && !loading && (
                    <TableRow>
                        <TableCell colSpan={6} align="center">
                            No hay contactos guardados.
                        </TableCell>
                    </TableRow>
                )}
                {contacts.map((contact) => {
                    const isSelected = selectedContacts.indexOf(contact.id) !== -1;
                    return (
                        <TableRow 
                            key={contact.id} 
                            hover
                            selected={isSelected}
                            onClick={() => handleDetailsClick(contact)}
                            style={{ cursor: 'pointer' }}
                        >
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isSelected}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(event) => handleSelectOne(event, contact.id)}
                                />
                            </TableCell>
                            <TableCell style={{ textTransform: 'uppercase' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar src={contact.profilePicUrl} alt={contact.name} style={{ marginRight: 10, width: 30, height: 30 }} />
                                    {contact.name}
                                </div>
                            </TableCell>
                            <TableCell>{contact.number}</TableCell>
                            <TableCell>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                {contact.labels?.map((label) => {
                                    const fullLabel = labels.find(l => l.id === label.id);
                                    const isSystemLabel = fullLabel ? fullLabel.isSystem : label.isSystem;
                                    const lblColor = isSystemLabel ? "#FFB09C" : (label.color || "#e0e0e0");
                                    return (
                                        <Chip
                                            key={label.id}
                                            label={label.name}
                                            size="small"
                                            style={{ 
                                                backgroundColor: lblColor, 
                                                color: getContrastText(lblColor)
                                            }}
                                            classes={{ label: classes.chipText, deleteIcon: classes.chipText }}
                                        />
                                    );
                                })}
                                </div>
                            </TableCell>
                            <TableCell>{format(new Date(contact.createdAt), 'dd/MM/yyyy')}</TableCell>
                            <TableCell>
                                {contact.whatsapp && (
                                    <Chip 
                                         label={contact.whatsapp.name} 
                                         size="small"
                                         style={{ 
                                             backgroundColor: getConnectionColor(whatsApps.findIndex(w => w.id === contact.whatsapp.id)),
                                             color: getContrastText(getConnectionColor(whatsApps.findIndex(w => w.id === contact.whatsapp.id)))
                                         }} 
                                         classes={{ label: classes.chipText, deleteIcon: classes.chipText }}
                                     />
                                )}
                            </TableCell>
                            <TableCell>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(contact); }}>
                                    <EditIcon />
                                </IconButton>
                                {user.profile === "admin" && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setContactToDelete(contact); setConfirmDeleteOpen(true); }}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
      </Paper>

    </MainContainer>
  );
}
